/**
 * Typed functions for `/api/disputes` endpoints.
 *
 * Source of truth for paths: src/api/routes/disputes.ts
 *
 * Wave A only wires the endpoints the Mini-App pages will actually call:
 * getDispute, submitEvidence, and openDispute (exposed from deals.ts
 * because its URL is keyed by deal shortId, not dispute UUID).
 */

import { apiGet, apiPost } from './client';
import type {
  AdminDisputeDetailResponse,
  AdminDisputeQueueResponse,
  DisputeResponse,
  EvidenceResponse,
  ListDisputeMessagesResponse,
  ListEvidenceResponse,
  PostDisputeMessageBody,
  PostDisputeMessageResponse,
  ResolveDisputeBody,
  ResolveDisputeResponse,
  SubmitEvidenceBody,
} from './types';

/** GET /disputes/:disputeId - fetch dispute detail. */
export async function getDispute(disputeId: string): Promise<DisputeResponse> {
  return apiGet<DisputeResponse>(`/disputes/${encodeURIComponent(disputeId)}`);
}

/** GET /disputes/:disputeId/evidence - list every evidence row, oldest first. */
export async function listEvidence(disputeId: string): Promise<ListEvidenceResponse> {
  return apiGet<ListEvidenceResponse>(
    `/disputes/${encodeURIComponent(disputeId)}/evidence`,
  );
}

/** POST /disputes/:disputeId/evidence - submit evidence before the deadline. */
export async function submitEvidence(
  disputeId: string,
  body: SubmitEvidenceBody,
): Promise<EvidenceResponse> {
  return apiPost<EvidenceResponse>(
    `/disputes/${encodeURIComponent(disputeId)}/evidence`,
    body,
  );
}

// ─── Dispute messages ──────────────────────────────────────

/** GET /disputes/:disputeId/messages - list all messages in the dispute thread. */
export async function listMessages(disputeId: string): Promise<ListDisputeMessagesResponse> {
  return apiGet<ListDisputeMessagesResponse>(
    `/disputes/${encodeURIComponent(disputeId)}/messages`,
  );
}

/** POST /disputes/:disputeId/messages - post a message in the dispute thread. */
export async function postMessage(
  disputeId: string,
  body: PostDisputeMessageBody,
): Promise<PostDisputeMessageResponse> {
  return apiPost<PostDisputeMessageResponse>(
    `/disputes/${encodeURIComponent(disputeId)}/messages`,
    body,
  );
}

// ─── Admin endpoints ───────────────────────────────────────

/**
 * GET /disputes/admin/queue — admin/arbiter only.
 *
 * Returns every dispute in `open` / `evidence` / `review` status enriched
 * with deal + party context. Backend role-gates this; non-admins get 401.
 */
export async function listAdminQueue(): Promise<AdminDisputeQueueResponse> {
  return apiGet<AdminDisputeQueueResponse>('/disputes/admin/queue');
}

/**
 * GET /disputes/admin/:disputeId — admin/arbiter only.
 *
 * Single dispute with deal, parties, and full evidence list in one call.
 */
export async function getAdminDispute(
  disputeId: string,
): Promise<AdminDisputeDetailResponse> {
  return apiGet<AdminDisputeDetailResponse>(
    `/disputes/admin/${encodeURIComponent(disputeId)}`,
  );
}

/**
 * POST /disputes/:disputeId/resolve — admin/arbiter only.
 *
 * Executes the on-chain resolve op via the backend. The discriminated
 * union ensures the `split` variant carries explicit amounts.
 */
export async function resolveDispute(
  disputeId: string,
  body: ResolveDisputeBody,
): Promise<ResolveDisputeResponse> {
  return apiPost<ResolveDisputeResponse>(
    `/disputes/${encodeURIComponent(disputeId)}/resolve`,
    body,
  );
}
